// exp_cli/src/app/utils/utils.js
import { differenceInSeconds, format, isValid, parseISO } from "date-fns";

export const convertHexToRGB = (hex) => {
  if (!hex) return null;

  // check if it's a rgba
  if (hex.match("rgba")) {
    let triplet = hex.slice(5).split(",").slice(0, -1).join(",");
    return triplet;
  }

  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let c = hex.substring(1).split("");

    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }

    c = "0x" + c.join("");

    return [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",");
  }

  return null;
};

export function getTimeDifference(date) {
  if (!date) return null;

  try {
    let difference = differenceInSeconds(new Date(), date);

    if (difference < 60) return `${Math.floor(difference)} sec`;
    else if (difference < 3600) return `${Math.floor(difference / 60)} min`;
    else if (difference < 86400) return `${Math.floor(difference / 3660)} h`;
    else if (difference < 86400 * 30) return `${Math.floor(difference / 86400)} d`;
    else if (difference < 86400 * 30 * 12) return `${Math.floor(difference / 86400 / 30)} mon`;
    else return `${(difference / 86400 / 30 / 12).toFixed(1)} y`;
  } catch (error) {
    console.error('Error in getTimeDifference:', error);
    return null;
  }
}

export function generateRandomId() {
  try {
    let tempId = Math.random().toString();
    let uid = tempId.substring(2, tempId.length - 1);
    return uid;
  } catch (error) {
    console.error('Error in generateRandomId:', error);
    return null;
  }
}

/**
 * Formats the current date and time as 'YYYY-MM-DD HH:mm:ss.SSS'
 * @returns {string|null} Formatted date and time string or null if error occurs
 */
export function formatCurrentDateTime() {
  try {
    return formatDateTime(new Date());
  } catch (error) {
    console.error('Error in formatCurrentDateTime:', error);
    return null;
  }
}

/**
 * Formats a given Date object as 'YYYY-MM-DD HH:mm:ss.SSS'
 * @param {Date} date - The Date object to format
 * @returns {string|null} Formatted date and time string or null if invalid input/error
 */
export function formatDateTime(date) {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }

  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
  } catch (error) {
    console.error('Error in formatDateTime:', error);
    return null;
  }
}

/**
 * Formats a given Date object as 'YYYY-MM-DD'
 * @param {Date} date - The Date object to format
 * @returns {string|null} Formatted date and time string or null if invalid input/error
 */
export function formatDateTimeDay(date) {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }

  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error in formatDateTimeDay:', error);
    return null;
  }
}

/**
 * Formats a given Date object as 'YYYY-MM-DD HH:mm:ss'
 * @param {Date} date - The Date object to format
 * @returns {string|null} Formatted date and time string or null if invalid input/error
 */
export function formatDateTimeSecond(date) {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }

  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('Error in formatDateTime:', error);
    return null;
  }
}

/**
 * Formats a given Date object as 'HH:mm:ss'
 * @param {Date} date - The Date object to format
 * @returns {string|null} Formatted date and time string or null if invalid input/error
 */
export function formatTime(date) {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }

  try {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('Error in formatDateTime:', error);
    return null;
  }
}

/**
 * Formats a date string as 'YYYY-MM-DD'
 * @param {string} date - The date string to format
 * @returns {string} Formatted date string or '-' if invalid input
 */
export function formatDateDayFromStr(date) {
  if (!date) return '-';

  try {
    const formatDate = (dateString) => {
      const parsedDate = parseISO(dateString);
      return isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : '-';
    };

    return formatDate(date);
  } catch (error) {
    console.error('Error in formatDate:', error);
    return '-';
  }
}

/**
 * Formats a date string as 'YYYY-MM-DD(요일)'
 * @param {string} date - The date string to format
 * @returns {string} Formatted date string with Korean day of week or '-' if invalid input
 */
export function formatDateWeekDayFromStr(date) {
  if (!date) return '-';

  try {
    const formatDate = (dateString) => {
      const parsedDate = parseISO(dateString);
      if (!isValid(parsedDate)) return '-';

      const daysInKorean = ['일', '월', '화', '수', '목', '금', '토'];
      const dayOfWeek = daysInKorean[parsedDate.getDay()];

      return format(parsedDate, 'yyyy-MM-dd') + `(${dayOfWeek})`;
    };

    return formatDate(date);
  } catch (error) {
    console.error('Error in formatDate:', error);
    return '-';
  }
}
/**
 * Formats a date string as 'YYYY-MM-DD HH:mm'
 * @param {string} date - The date string to format
 * @returns {string} Formatted date string or '-' if invalid input
 */
export function formatDateTimeMinFromStr(date) {
  if (!date) return '-';

  try {
    const formatDate = (dateString) => {
      const parsedDate = parseISO(dateString);
      return isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd HH:mm') : '-';
    };

    return formatDate(date);
  } catch (error) {
    console.error('Error in formatDateTimeMin:', error);
    return '-';
  }
}

/**
 * Finds the date of the last Tuesday before the given date
 * @param {Date} date - The reference date to find the last Tuesday before
 * @returns {Date} Date object representing the last Tuesday
 * @throws {Error} If input is not a valid date
 */
export function findLastTuesdayToStr(date) {
  // Validate input
  if (!(date instanceof Date) || isNaN(date)) {
    throw new Error('Invalid date provided');
  }

  // Create a copy of the date to avoid modifying the input
  const targetDate = new Date(date);

  // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const currentDay = targetDate.getDay();

  // Calculate days to subtract to reach the last Tuesday
  let daysToSubtract;

  if (currentDay <= 2) { // Sunday (0), Monday (1), Tuesday (2)
    daysToSubtract = currentDay + 7 - 2;
  } else { // Wednesday (3) through Saturday (6)
    daysToSubtract = currentDay - 2;
  }

  // Subtract the calculated days
  targetDate.setDate(targetDate.getDate() - daysToSubtract);

  // Reset time to midnight to ensure consistent results
  targetDate.setHours(0, 0, 0, 0);

  const daysOfWeek = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth() + 1; // Months are zero-indexed
  const day = targetDate.getDate();
  const dayOfWeek = daysOfWeek[targetDate.getDay()];

  return `${year}년 ${month}월 ${day}일(${dayOfWeek})`;
}

/**
 * Finds the date of the last Tuesday before the given date
 * @param {Date} date - The reference date to find the last Tuesday before
 * @returns {Date} Date object representing the last Tuesday
 * @throws {Error} If input is not a valid date
 */
export function findLastTuesday(date) {
  // Validate input
  if (!(date instanceof Date) || isNaN(date)) {
    throw new Error('Invalid date provided');
  }

  // Create a copy of the date to avoid modifying the input
  const targetDate = new Date(date);

  // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const currentDay = targetDate.getDay();

  // Calculate days to subtract to reach the last Tuesday
  let daysToSubtract;

  if (currentDay <= 2) { // Sunday (0), Monday (1), Tuesday (2)
    daysToSubtract = currentDay + 7 - 2;
  } else { // Wednesday (3) through Saturday (6)
    daysToSubtract = currentDay - 2;
  }

  // Subtract the calculated days
  targetDate.setDate(targetDate.getDate() - daysToSubtract);

  // Reset time to midnight to ensure consistent results
  targetDate.setHours(0, 0, 0, 0);

  return targetDate; // Return the Date object instead of the timestamp
}

// // Example usage:
// const today = new Date();
// const lastTuesday = findLastTuesday(today);
// console.log('Last Tuesday:', lastTuesday.toDateString());

// // More examples:
// const specificDate = new Date('2024-11-27');
// console.log('Last Tuesday before Nov 27, 2024:',
//     findLastTuesday(specificDate).toDateString());

// // If today is Tuesday, it will return the previous Tuesday
// const tuesdayDate = new Date('2024-11-26'); // A Tuesday
// console.log('Last Tuesday before a Tuesday:',
//     findLastTuesday(tuesdayDate).toDateString());

export function formatDateToKST(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const kstDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));

  const year = kstDate.getFullYear();
  const month = String(kstDate.getMonth() + 1).padStart(2, '0');
  const day = String(kstDate.getDate()).padStart(2, '0');
  const hours = String(kstDate.getHours()).padStart(2, '0');
  const minutes = String(kstDate.getMinutes()).padStart(2, '0');
  const seconds = String(kstDate.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function formatDateToKST_filename(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const kstDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));

  const year = kstDate.getFullYear();
  const month = String(kstDate.getMonth() + 1).padStart(2, '0');
  const day = String(kstDate.getDate()).padStart(2, '0');
  const hours = String(kstDate.getHours()).padStart(2, '0');
  const minutes = String(kstDate.getMinutes()).padStart(2, '0');
  const seconds = String(kstDate.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}_${hours}${minutes}${seconds}`;
}

export function formatDateToKST_filename_more(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const kstDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));

  const year = kstDate.getFullYear();
  const month = String(kstDate.getMonth() + 1).padStart(2, '0');
  const day = String(kstDate.getDate()).padStart(2, '0');
  const hours = String(kstDate.getHours()).padStart(2, '0');
  const minutes = String(kstDate.getMinutes()).padStart(2, '0');
  const seconds = String(kstDate.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

/**
 * Validates a required env var value, throwing immediately instead of
 * silently falling back to a default when it is missing.
 * CRA inlines `process.env.REACT_APP_X` at build time via static text
 * replacement, so the value must be read at the call site (not looked up
 * dynamically by name here) — this only validates it and improves the
 * error message.
 * @param {string} value - The result of `process.env.REACT_APP_X`
 * @param {string} name - The env var name, for the error message
 * @returns {string} The validated value
 * @throws {Error} If the value is missing
 */
export function requireEnv(value, name) {
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
}

export const getPathByCdExt = (rid_cd_ext) => {
  switch (rid_cd_ext?.trim()) {
    case '41':
      return 'leet'
    case '42':
      return 'g5'
    case '43':
      return 'g7'
    case '44':
      return 'g72'
    case '45':
      return 'bar'
    default:
      return ''
  }
}